import {
    ChartPatternStrictnessTier,
    getApprovedChartPatternDurations,
    type BarDuration,
    type ChartPatternDisplayMode,
    type ChartPatternKey,
    type ChartPatternResolvedEvent,
    type ChartPatternResolvedOccurrence,
    type ChartPatternResolvedSnapshot,
    type ChartPatternSummary
} from '@einhaus/project314-types';
import { getUnixTime } from '@einhaus/meteoric-helpers';

export const CHART_PATTERN_SUPPORTED_DURATIONS: BarDuration[] = getApprovedChartPatternDurations();

export const isChartPatternDurationSupported = (duration: BarDuration): boolean => CHART_PATTERN_SUPPORTED_DURATIONS.includes(duration);

const CHART_PATTERN_PRICE_DEDUPE_TOLERANCE_PCT = 0.015;

const getAnchoredWindow = (
    item: Pick<ChartPatternResolvedSnapshot, 'anchors'> | Pick<ChartPatternResolvedOccurrence, 'anchors'>
): { startTimestamp: number; endTimestamp: number } | null => {
    if (!item.anchors.length) {
        return null;
    }

    const sortedAnchors = [...item.anchors].sort((left, right) => left.timestamp - right.timestamp);
    const firstAnchor = sortedAnchors[0];
    const lastAnchor = sortedAnchors[sortedAnchors.length - 1];

    return {
        startTimestamp: firstAnchor.timestamp,
        endTimestamp: lastAnchor.timestamp
    };
};

const windowsOverlap = (
    leftWindow: { startTimestamp: number; endTimestamp: number } | null,
    rightWindow: { startTimestamp: number; endTimestamp: number } | null
): boolean => {
    if (!leftWindow || !rightWindow) {
        return false;
    }

    return Math.max(leftWindow.startTimestamp, rightWindow.startTimestamp) <= Math.min(leftWindow.endTimestamp, rightWindow.endTimestamp);
};

const pricesAreClose = (leftPrice: number | null, rightPrice: number | null): boolean => {
    if (leftPrice == null || rightPrice == null) {
        return false;
    }

    const referencePrice = Math.max(Math.abs(leftPrice), Math.abs(rightPrice), 1);
    return Math.abs(leftPrice - rightPrice) / referencePrice <= CHART_PATTERN_PRICE_DEDUPE_TOLERANCE_PCT;
};

const getStrictnessScore = (strictnessTier: ChartPatternResolvedSnapshot['strictnessTier']): number => {
    switch (strictnessTier) {
        case null:
            return 0;
        case ChartPatternStrictnessTier.strict:
            return 3;
        case ChartPatternStrictnessTier.standard:
            return 2;
        case ChartPatternStrictnessTier.loose:
            return 1;
    }
};

const compareChartPatternSnapshotsForDedupe = (left: ChartPatternResolvedSnapshot, right: ChartPatternResolvedSnapshot): number => {
    const qualityDelta = (right.qualityScore ?? 0) - (left.qualityScore ?? 0);
    if (qualityDelta !== 0) return qualityDelta;

    const strictnessDelta = getStrictnessScore(right.strictnessTier) - getStrictnessScore(left.strictnessTier);
    if (strictnessDelta !== 0) return strictnessDelta;

    return getUnixTime(right.snapshotDateTime) - getUnixTime(left.snapshotDateTime);
};

export const dedupeChartPatternSnapshots = (snapshots: ChartPatternResolvedSnapshot[]): ChartPatternResolvedSnapshot[] => {
    const sortedSnapshots = [...snapshots].sort(compareChartPatternSnapshotsForDedupe);
    const selectedSnapshots: ChartPatternResolvedSnapshot[] = [];

    for (const snapshot of sortedSnapshots) {
        const snapshotWindow = getAnchoredWindow(snapshot);

        const isDuplicate = selectedSnapshots.some((selectedSnapshot) => {
            if (selectedSnapshot.patternKey !== snapshot.patternKey || selectedSnapshot.direction !== snapshot.direction) {
                return false;
            }

            return (
                windowsOverlap(snapshotWindow, getAnchoredWindow(selectedSnapshot)) &&
                pricesAreClose(
                    snapshot.breakoutPrice ?? snapshot.resistancePrice,
                    selectedSnapshot.breakoutPrice ?? selectedSnapshot.resistancePrice
                )
            );
        });

        if (!isDuplicate) {
            selectedSnapshots.push(snapshot);
        }
    }

    return selectedSnapshots.sort((left, right) => getUnixTime(left.snapshotDateTime) - getUnixTime(right.snapshotDateTime));
};

const compareChartPatternOccurrencesForDedupe = (left: ChartPatternResolvedOccurrence, right: ChartPatternResolvedOccurrence): number => {
    const qualityDelta = (right.qualityScore ?? 0) - (left.qualityScore ?? 0);
    if (qualityDelta !== 0) return qualityDelta;

    const strictnessDelta = getStrictnessScore(right.strictnessTier) - getStrictnessScore(left.strictnessTier);
    if (strictnessDelta !== 0) return strictnessDelta;

    return getOccurrenceSortTimestamp(right) - getOccurrenceSortTimestamp(left);
};

const getOccurrenceSortTimestamp = (occurrence: ChartPatternResolvedOccurrence): number => {
    let sortDateTime = '1970-01-01 00:00:00';

    if (occurrence.triggeredDateTime) {
        sortDateTime = occurrence.triggeredDateTime;
    } else if (occurrence.failedDateTime) {
        sortDateTime = occurrence.failedDateTime;
    } else if (occurrence.expiredDateTime) {
        sortDateTime = occurrence.expiredDateTime;
    } else if (occurrence.formedDateTime) {
        sortDateTime = occurrence.formedDateTime;
    } else if (occurrence.availableFromDateTime) {
        sortDateTime = occurrence.availableFromDateTime;
    } else if (occurrence.lastSeenDateTime) {
        sortDateTime = occurrence.lastSeenDateTime;
    }

    return getUnixTime(sortDateTime);
};

export const dedupeChartPatternOccurrences = (occurrences: ChartPatternResolvedOccurrence[]): ChartPatternResolvedOccurrence[] => {
    const sortedOccurrences = [...occurrences].sort(compareChartPatternOccurrencesForDedupe);
    const selectedOccurrences: ChartPatternResolvedOccurrence[] = [];

    for (const occurrence of sortedOccurrences) {
        const occurrenceWindow = getAnchoredWindow(occurrence);

        const isDuplicate = selectedOccurrences.some((selectedOccurrence) => {
            if (selectedOccurrence.patternKey !== occurrence.patternKey || selectedOccurrence.direction !== occurrence.direction) {
                return false;
            }

            return (
                windowsOverlap(occurrenceWindow, getAnchoredWindow(selectedOccurrence)) &&
                pricesAreClose(
                    occurrence.breakoutPrice ?? occurrence.resistancePrice,
                    selectedOccurrence.breakoutPrice ?? selectedOccurrence.resistancePrice
                )
            );
        });

        if (!isDuplicate) {
            selectedOccurrences.push(occurrence);
        }
    }

    return selectedOccurrences.sort((left, right) => getOccurrenceSortTimestamp(left) - getOccurrenceSortTimestamp(right));
};

export const getSelectedChartPatternSnapshotsForDuration = (
    summary: ChartPatternSummary | null,
    duration: BarDuration,
    chartPatternKeyFilter?: ChartPatternKey | null
): ChartPatternResolvedSnapshot[] => {
    const selectedForDuration = summary?.selected?.[duration];

    if (!selectedForDuration) {
        return [];
    }

    return Object.values(selectedForDuration)
        .flatMap((byDirection) => Object.values(byDirection ?? {}))
        .filter((snapshot): snapshot is ChartPatternResolvedSnapshot => snapshot != null)
        .filter((snapshot) => (chartPatternKeyFilter ? snapshot.patternKey === chartPatternKeyFilter : true));
};

export const getChartPatternSnapshotsForDisplay = (config: {
    summary: ChartPatternSummary | null;
    snapshots: ChartPatternResolvedSnapshot[];
    duration: BarDuration;
    chartPatternDisplayMode: ChartPatternDisplayMode;
    chartPatternKeyFilter?: ChartPatternKey | null;
    focusedChartPatternOccurrenceId?: number | null;
}): {
    selectedSnapshots: ChartPatternResolvedSnapshot[];
    snapshotsToRender: ChartPatternResolvedSnapshot[];
    selectedOccurrenceIds: Set<number>;
} => {
    const { summary, snapshots, duration, chartPatternDisplayMode, chartPatternKeyFilter, focusedChartPatternOccurrenceId } = config;

    const filteredSnapshots = chartPatternKeyFilter
        ? snapshots.filter((snapshot) => snapshot.patternKey === chartPatternKeyFilter)
        : snapshots;

    const dedupedSnapshots = dedupeChartPatternSnapshots(filteredSnapshots);

    const selectedSnapshots = getSelectedChartPatternSnapshotsForDuration(summary, duration, chartPatternKeyFilter);
    const selectedOccurrenceIds = new Set(selectedSnapshots.map((snapshot) => snapshot.occurrenceId));

    let snapshotsToRender: ChartPatternResolvedSnapshot[];

    switch (chartPatternDisplayMode) {
        case 'allCurrent':
            snapshotsToRender = dedupedSnapshots;
            break;
        case 'historicalOccurrences':
            snapshotsToRender = [];
            break;
        case 'pitReplay':
        case 'focused':
            snapshotsToRender =
                focusedChartPatternOccurrenceId == null
                    ? []
                    : filteredSnapshots.filter((snapshot) => snapshot.occurrenceId === focusedChartPatternOccurrenceId);

            break;
        case 'selected':
        default:
            snapshotsToRender = selectedSnapshots;
            break;
    }

    return {
        selectedSnapshots,
        snapshotsToRender,
        selectedOccurrenceIds
    };
};

export const getChartPatternSnapshotSeriesForDisplay = (config: {
    summary: ChartPatternSummary | null;
    snapshots: ChartPatternResolvedSnapshot[];
    snapshotSeries: ChartPatternResolvedSnapshot[];
    duration: BarDuration;
    chartPatternDisplayMode: ChartPatternDisplayMode;
    chartPatternKeyFilter?: ChartPatternKey | null;
    focusedChartPatternOccurrenceId?: number | null;
}): ChartPatternResolvedSnapshot[] => {
    const { snapshotsToRender } = getChartPatternSnapshotsForDisplay({
        summary: config.summary,
        snapshots: config.snapshots,
        duration: config.duration,
        chartPatternDisplayMode: config.chartPatternDisplayMode,
        chartPatternKeyFilter: config.chartPatternKeyFilter,
        focusedChartPatternOccurrenceId: config.focusedChartPatternOccurrenceId
    });

    const occurrenceIds = new Set(snapshotsToRender.map((snapshot) => snapshot.occurrenceId));

    if (occurrenceIds.size === 0) {
        return [];
    }

    return config.snapshotSeries
        .filter((snapshot) => occurrenceIds.has(snapshot.occurrenceId))
        .sort((left, right) => getUnixTime(left.snapshotDateTime) - getUnixTime(right.snapshotDateTime));
};

export const getChartPatternHistoricalOccurrencesForDisplay = (config: {
    occurrences: ChartPatternResolvedOccurrence[];
    duration: BarDuration;
    chartPatternKeyFilter?: ChartPatternKey | null;
}): ChartPatternResolvedOccurrence[] => {
    const filteredOccurrences = config.occurrences.filter(
        (occurrence) =>
            occurrence.barDuration === config.duration &&
            (config.chartPatternKeyFilter ? occurrence.patternKey === config.chartPatternKeyFilter : true)
    );

    return dedupeChartPatternOccurrences(filteredOccurrences);
};

export const getChartPatternHistoricalEventsForDisplay = (config: {
    events: ChartPatternResolvedEvent[];
    duration: BarDuration;
    chartPatternKeyFilter?: ChartPatternKey | null;
    occurrenceIds?: Set<number>;
}): ChartPatternResolvedEvent[] => {
    const { events, duration, chartPatternKeyFilter, occurrenceIds } = config;

    return events
        .filter(
            (event) =>
                event.barDuration === duration &&
                (chartPatternKeyFilter ? event.patternKey === chartPatternKeyFilter : true) &&
                (occurrenceIds ? occurrenceIds.has(event.occurrenceId) : true)
        )
        .sort((a, b) => getUnixTime(b.eventDateTime) - getUnixTime(a.eventDateTime));
};

export const getChartPatternEventsForDisplay = (config: {
    summary: ChartPatternSummary | null;
    duration: BarDuration;
    chartPatternKeyFilter?: ChartPatternKey | null;
    occurrenceIds: Set<number>;
}): ChartPatternResolvedEvent[] => {
    const { summary, duration, chartPatternKeyFilter, occurrenceIds } = config;

    if (!summary?.events.length || occurrenceIds.size === 0) {
        return [];
    }

    return summary.events
        .filter(
            (event) =>
                event.barDuration === duration &&
                occurrenceIds.has(event.occurrenceId) &&
                (chartPatternKeyFilter ? event.patternKey === chartPatternKeyFilter : true)
        )
        .sort((a, b) => getUnixTime(b.eventDateTime) - getUnixTime(a.eventDateTime));
};
